// These routes stay outside the "Open Pages" tab system because they are auth/guard flows.
const EXCLUDED_APP_PATH_PREFIXES = ["/2fa"];
const EXCLUDED_APP_PATHS = ["/login", "/forbidden"];
const EXCLUDED_PROTOCOL_PREFIXES = ["mailto:", "tel:", "blob:", "data:"];
const INTERNAL_TABS_TEMP_DISABLED = true;

// App.vue registers itself here so helper code can open background tabs without relying on $root.
let registeredTabHost = null;

function hasTabApi(component) {
  return !!(
    component &&
    typeof component.upsertTab === "function" &&
    typeof component.shouldTrackRoute === "function" &&
    typeof component.persistOpenTabs === "function"
  );
}

export function registerTabHost(host) {
  if (!hasTabApi(host)) {
    registeredTabHost = null;
    return false;
  }

  registeredTabHost = host;
  return true;
}

export function unregisterTabHost(host) {
  if (!host || registeredTabHost === host) {
    registeredTabHost = null;
  }
}

export function getRegisteredTabHost() {
  return registeredTabHost;
}

function toURL(target) {
  if (!target || typeof target !== "string") {
    return null;
  }

  const value = target.trim();
  if (!value) {
    return null;
  }

  const lowerValue = value.toLowerCase();
  const isExcludedProtocol = EXCLUDED_PROTOCOL_PREFIXES.some((prefix) =>
    lowerValue.startsWith(prefix)
  );
  if (isExcludedProtocol) {
    return null;
  }

  try {
    return new URL(value, window.location.origin);
  } catch (error) {
    return null;
  }
}

function normalizeResolvedPath(resolvedURL) {
  if (!resolvedURL) {
    return null;
  }

  return `${resolvedURL.pathname}${resolvedURL.search}${resolvedURL.hash}`;
}

function isTrackableAppPath(pathname) {
  if (!pathname) {
    return false;
  }

  if (EXCLUDED_APP_PATHS.includes(pathname)) {
    return false;
  }

  return !EXCLUDED_APP_PATH_PREFIXES.some((prefix) => pathname.indexOf(prefix) === 0);
}

function isTrackableRoute(route) {
  if (!route) {
    return false;
  }

  const path = route.path || "";
  return isTrackableAppPath(path);
}

function shouldTrackRoute(route) {
  const host = getRegisteredTabHost();
  if (host && typeof host.shouldTrackRoute === "function") {
    return host.shouldTrackRoute(route);
  }

  // Fallback used before the host is available or in legacy paths.
  return isTrackableRoute(route);
}

export function resolveInternalRouteLike(router, routeLike) {
  if (!router || !routeLike) {
    return null;
  }

  const resolved = router.resolve(routeLike);
  const hasMatchedRoute =
    resolved &&
    resolved.route &&
    Array.isArray(resolved.route.matched) &&
    resolved.route.matched.length > 0;

  if (!hasMatchedRoute) {
    return null;
  }

  if (!shouldTrackRoute(resolved.route)) {
    return null;
  }

  return resolved.route;
}

function resolveRouteLikeAny(router, routeLike) {
  if (!router || !routeLike) {
    return null;
  }

  const resolved = router.resolve(routeLike);
  const hasMatchedRoute =
    resolved &&
    resolved.route &&
    Array.isArray(resolved.route.matched) &&
    resolved.route.matched.length > 0;

  if (!hasMatchedRoute) {
    return null;
  }

  return resolved.route;
}

function resolveInternalTarget(router, target) {
  // Browser-like APIs (window.open / anchor href) give us strings, so normalize them back into
  // a router route only when they point to the same SPA and are eligible for app-tab tracking.
  const resolvedURL = toURL(target);
  if (!resolvedURL || resolvedURL.origin !== window.location.origin) {
    return null;
  }

  const resolvedPath = normalizeResolvedPath(resolvedURL);
  if (!isTrackableAppPath(resolvedURL.pathname)) {
    return null;
  }

  return resolveInternalRouteLike(router, resolvedPath);
}

function resolveInternalTargetAny(router, target) {
  const resolvedURL = toURL(target);
  if (!resolvedURL || resolvedURL.origin !== window.location.origin) {
    return null;
  }

  const resolvedPath = normalizeResolvedPath(resolvedURL);
  return resolveRouteLikeAny(router, resolvedPath);
}

export function isBackgroundIntent(event) {
  if (!event || event.shiftKey || event.altKey) {
    return false;
  }

  const isMiddleAuxClick = event.type === "auxclick" && event.button === 1;
  const isModifiedPrimaryClick =
    event.button === 0 && (event.ctrlKey || event.metaKey);

  return isMiddleAuxClick || isModifiedPrimaryClick;
}

export function openForeground(router, routeLike) {
  const route = INTERNAL_TABS_TEMP_DISABLED
    ? resolveRouteLikeAny(router, routeLike)
    : resolveInternalRouteLike(router, routeLike);
  if (!route) {
    return false;
  }

  router.push(route).catch(() => {});
  return true;
}

export function openBackground(router, routeLike) {
  // TEMP_INTERNAL_TABS_OFF_START
  if (INTERNAL_TABS_TEMP_DISABLED) {
    return false;
  }
  // TEMP_INTERNAL_TABS_OFF_END

  const route = resolveInternalRouteLike(router, routeLike);
  if (!route) {
    return false;
  }

  // Background open means: add/focus the route in the app tab bar without navigating away
  // from the current foreground page.
  const host = getRegisteredTabHost();
  if (!hasTabApi(host)) {
    return false;
  }

  host.upsertTab(route);
  host.persistOpenTabs();
  return true;
}

export function openFromClick(router, routeLike, event) {
  if (isBackgroundIntent(event)) {
    return openBackground(router, routeLike);
  }

  return openForeground(router, routeLike);
}

export function openWindowLike(router, target, openTarget) {
  // This is the compatibility layer used by window.open / target="_blank" interception:
  // _self -> foreground SPA navigation, _blank -> background app tab.
  const route = INTERNAL_TABS_TEMP_DISABLED
    ? resolveInternalTargetAny(router, target)
    : resolveInternalTarget(router, target);
  if (!route) {
    return false;
  }

  if (!openTarget || openTarget === "_self") {
    return openForeground(router, route);
  }

  if (openTarget === "_blank") {
    // TEMP_INTERNAL_TABS_OFF_START
    if (INTERNAL_TABS_TEMP_DISABLED) {
      return false;
    }
    // TEMP_INTERNAL_TABS_OFF_END

    return openBackground(router, route);
  }

  return false;
}
